'use client';

import { type JSX } from 'react';

import { useKeyPressEvent } from 'react-use';

import Button from '@/components/ui/Button';
import HStack from '@/components/ui/HStack';
import Link from '@/components/ui/Link';
import NewCheckbox from '@/components/ui/NewCheckbox';
import NewTextInput from '@/components/ui/NewTextInput';
import Text from '@/components/ui/Text';
import appIcons from '@/constants/appIcons';
import useI18n from '@/hooks/useI18n';

import useLogin from '../hooks/useLogin';
import { authRoutes } from '../routes/authRoutes';

export default function LoginForm(): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { t } = useI18n();

    const { isValid, isLoading, error, validationErrors, submit, register, setValue, watch } = useLogin();

    const { rememberMe } = watch();

    // ----------------------------------------------------------------------------------------------------
    // MARK: Side Effects
    // ----------------------------------------------------------------------------------------------------
    // Press on enter to submit
    useKeyPressEvent('Enter', () => {
        if (isValid && !isLoading) {
            submit();
        }
    });

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <div className="space-y-10">
            <div className="space-y-6">
                <div className="space-y-3">
                    <NewTextInput
                        register={register('identifier')}
                        type="text"
                        label={t('Auth.Identifier_Label')}
                        placeholder={t('Auth.Identifier_Placeholder')}
                        prefixIcon={appIcons.outline.user}
                        errorMessage={validationErrors.identifier?.message}
                        isRequired
                    />

                    <NewTextInput
                        register={register('password')}
                        type="password"
                        label={t('Auth.Password_Label')}
                        placeholder={t('Auth.Password_Placeholder')}
                        prefixIcon={appIcons.outline.password}
                        errorMessage={validationErrors.password?.message}
                        isRequired
                    />
                </div>

                <HStack justify="between" alignment="center">
                    <NewCheckbox
                        label={t('Auth.Remember_Me_Title')}
                        value={rememberMe}
                        onChange={() => setValue('rememberMe', !rememberMe)}
                    />
                    <Link href={authRoutes.app.forgotPassword()} color="accent">
                        {t('Auth.Forgot_Password')}
                    </Link>
                </HStack>

                {/* Error Message */}
                {error && (
                    <Text size="2xs" color="danger">
                        {error.message}
                    </Text>
                )}

                <Button verticalPadding="lg" width="full" isLoading={isLoading} isDisabled={!isValid || isLoading} onClick={submit}>
                    {t('Auth.Login')}
                </Button>
            </div>

            <HStack justify="center" className="w-full space-x-1">
                <Text size="sm">{t('Auth.Do_Not_Have_Account_Yet')}</Text>
                <Link href={authRoutes.app.register()} color="accent">
                    {t('Auth.Register')}
                </Link>
            </HStack>
        </div>
    );
}
